<template>
  <div>
    <div class="physical-assessment-container">
      <p class="assessment-caption">
        Click to add a detailed description of a region.
      </p>
      <div class="assessment-columns">
        <div class="assessment-column">
          <h2>Anterior</h2>
          <div class="physical-assessment-locations">
            <img
              :src="physicalAssessmentImages.anterior.paperDoll.image"
              :style="physicalAssessmentImages.anterior.paperDoll.styles"
              alt="Anterior"
            >
            <div v-for="(location, locationKey) in physicalAssessmentImages.anterior.locations"
                 :key="locationKey"
                 class="assessment-location"
                 :style="location.styles"
            >
              <img
                :src="location.image"
                class="assessment-location-image"
                :class="locationImageClasses(locationKey)"
                @mouseover="mouseOverLocation(locationKey)"
                @mouseleave="mouseLeaveLocation(locationKey)"
                @click="openLocation(locationKey)"
              >
              <md-tooltip :md-direction="location.tooltipPlacement" :md-arrow="true" :class="{'no-arrow': location.disableArrow}">
                {{ physicalAssessmentLocations[locationKey].label[language] }}
              </md-tooltip>
            </div>
          </div>
        </div>
        <div class="assessment-column">
          <h2>Posterior</h2>
          <div class="physical-assessment-locations">
            <img
              :src="physicalAssessmentImages.posterior.paperDoll.image"
              :style="physicalAssessmentImages.anterior.paperDoll.styles"
              alt="Posterior"
            >
            <div
              v-for="(location, locationKey) in physicalAssessmentImages.posterior.locations"
              :key="locationKey"
              class="assessment-location"
              :style="location.styles"
            >
              <img
                :src="location.image"
                class="assessment-location-image"
                :class="locationImageClasses(locationKey)"
                @mouseover="mouseOverLocation(locationKey)"
                @mouseleave="mouseLeaveLocation(locationKey)"
                @click="openLocation(locationKey)"
              >
              <md-tooltip :md-direction="location.tooltipPlacement" :md-arrow="true" :class="{'no-arrow': location.disableArrow}">
                {{ physicalAssessmentLocations[locationKey].label[language] }}
              </md-tooltip>
            </div>
          </div>
        </div>
        <div class="assessment-column">
          <h2>Spine</h2>
          <case-spine-assessment
            :spinal-assessments="caseConf.spinalAssessments"
            :readonly="readonly"
            @update="updateSpineAssessment"
          />
        </div>
      </div>
    </div>

    <md-drawer
      class="md-right patient-condition-drawer"
      :md-fixed="true"
      :md-active.sync="state.isDrawerOpen"
      @md-closed="closeModal"
    >
      <md-dialog-content>
        <div class="physical-assessment-container">
          <section-title color="blue-4">
            Patient Condition Editor
          </section-title>

          <div class="assessment-regions">
            <h2>Anterior</h2>
            <h2>Posterior</h2>
          </div>

          <div class="assessment-regions">
            <div class="physical-assessment-locations">
              <img
                :src="physicalAssessmentImages.anterior.paperDoll.image"
                :style="physicalAssessmentImages.anterior.paperDoll.styles"
                alt="Anterior"
              >
              <div v-for="(location, locationKey) in physicalAssessmentImages.anterior.locations"
                   :key="locationKey"
                   class="assessment-location"
                   :style="location.styles"
              >
                <img
                  :src="location.image"
                  class="assessment-location-image"
                  :class="locationImageClasses(locationKey)"
                  @mouseover="mouseOverLocation(locationKey)"
                  @mouseleave="mouseLeaveLocation(locationKey)"
                  @click="openLocation(locationKey)"
                >
                <md-tooltip :md-direction="location.tooltipPlacement" :md-arrow="true" :class="{'no-arrow': location.disableArrow}">
                  {{ physicalAssessmentLocations[locationKey].label[language] }}
                </md-tooltip>
              </div>
            </div>

            <div class="physical-assessment-locations">
              <img
                :src="physicalAssessmentImages.posterior.paperDoll.image"
                :style="physicalAssessmentImages.anterior.paperDoll.styles"
                alt="Posterior"
              >
              <div v-for="(location, locationKey) in physicalAssessmentImages.posterior.locations"
                   :key="locationKey"
                   class="assessment-location"
                   :style="location.styles"
              >
                <img
                  :src="location.image"
                  class="assessment-location-image"
                  :class="locationImageClasses(locationKey)"
                  @mouseover="mouseOverLocation(locationKey)"
                  @mouseleave="mouseLeaveLocation(locationKey)"
                  @click="openLocation(locationKey)"
                >
                <md-tooltip :md-direction="location.tooltipPlacement" :md-arrow="true" :class="{'no-arrow': location.disableArrow}">
                  {{ physicalAssessmentLocations[locationKey].label[language] }}
                </md-tooltip>
              </div>
            </div>
          </div>
        </div>
      </md-dialog-content>

      <hr class="divider">

      <md-dialog-content class="lower-drawer-content">
        <section-title class="section-title" color="blue-4">
          {{ state.currentLocationKey && physicalAssessmentLocations[state.currentLocationKey].label[language] }}
        </section-title>

        <div class="container">
          <label class="assessment-caption">
            Enter information about this part of the patient's body:
          </label>
          <md-field>
            <md-textarea
              v-model="state.assessment"
              class="assessment-input"
              :disabled="readonly"
              :maxlength="256"
              @input="value => updateAssessment(value.trim())"
            />
          </md-field>
        </div>

        <div class="moulage-container">
          <section-title class="section-title" color="blue-4">
            Moulage Options
          </section-title>
          <div class="container">
            <label class="assessment-caption">Type of Moulage</label>
            <md-field class="category-select">
              <md-select
                v-model="state.currentMoulageCategory"
                :disabled="!moulageCategories.length || readonly"
              >
                <md-option
                  v-for="category in moulageCategories"
                  :key="category"
                  :value="category"
                >
                  {{ humanize(category) }}
                </md-option>
                <md-option
                  v-if="currentLocationPhysicalRepercussion && currentLocationPhysicalRepercussion.type === 'FORCED_MOULAGE'"
                >
                  {{ humanize(currentLocationPhysicalRepercussion.metadata.moulage.category) }}
                </md-option>
              </md-select>
            </md-field>
            <div
              v-for="variation in moulageVariations"
              :key="variation"
            >
              <label class="assessment-caption">{{ humanize(variation) }}</label>
              <div class="moulage-group">
                <div
                  v-for="moulage in moulagesByCategoryAndLocationAndVariation[variation]"
                  :key="moulage.id"
                  class="moulage-button"
                  :class="{ 'selected': moulageSelected(moulage)}"
                  @click="selectMoulage(moulage)"
                >
                  <img
                    class="moulage-thumbnail"
                    :class="{ 'thumbnail-disabled': moulageDisabled(moulage).isDisabled }"
                    :src="thumbnailFor(moulage)"
                  >
                  <md-tooltip v-if="moulageDisabled(moulage).isDisabled">
                    {{ moulageDisabled(moulage).message }}
                  </md-tooltip>
                  <md-button
                    class="expand-preview-button md-icon-button"
                    @click.stop="expandPreview(moulage)"
                  >
                    <md-icon>zoom_in</md-icon>
                  </md-button>

                  <span
                    v-if="moulagesByCategoryAndLocationAndVariation[variation].length > 1"
                  >{{ labelFor(moulage) }}</span>
                </div>
              </div>
            </div>
            <div v-if="currentLocationPhysicalRepercussion && currentLocationPhysicalRepercussion.type === 'FORCED_MOULAGE'">
              <label class="assessment-caption">{{ humanize(currentLocationPhysicalRepercussion.metadata.moulage.variation) }}</label>
              <div
                class="moulage-button selected"
              >
                <img class="moulage-thumbnail" :src="thumbnailFor(currentLocationPhysicalRepercussion.metadata.moulage, currentLocationPhysicalRepercussion.location)">

                <md-button
                  class="expand-preview-button md-icon-button"
                  @click.stop="expandPreview(currentLocationPhysicalRepercussion.metadata.moulage, currentLocationPhysicalRepercussion.location)"
                >
                  <md-icon>zoom_in</md-icon>
                </md-button>

                <span>{{ labelFor(currentLocationPhysicalRepercussion.metadata.moulage) }}</span>
              </div>
            </div>
          </div>
        </div>
      </md-dialog-content>

      <md-dialog-actions>
        <md-button class="md-primary md-raised" @click="closeModal">DONE</md-button>
      </md-dialog-actions>
    </md-drawer>

    <md-dialog
      :md-active.sync="state.expandPreviewOpen"
      :md-fullscreen="false"
    >
      <md-dialog-content>
        <img class="moulage-expanded" :src="pictureFor(state.expandedMoulage, state.expandedLocation)">
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary md-raised" @click="state.expandPreviewOpen = false">DONE</md-button>
      </md-dialog-actions>
    </md-dialog>

    <md-dialog
      :md-active.sync="state.override.modalOpen"
      :md-fullscreen="false"
    >
      <md-dialog-title>Override ?</md-dialog-title>
      <md-dialog-content>
        Selecting this moulage will overwrite a previous selection ({{ state.override.location }})
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="closeOverrideModal(false)">Cancel</md-button>
        <md-button class="md-primary md-raised" @click="closeOverrideModal(true)">Continue</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>

import {
  capitalize,
  keys,
  find,
  groupBy,
  get,
  debounce,
  cloneDeep,
  keyBy,
  omitBy,
  invert,
  map,
  sortBy,
  merge,
} from 'lodash';
import { positionLabel, bodyTypeLabel } from '~lib/common/utils/labels';
import isInvalidMoulage from '~lib/common/config/invalidMoulageCombos';
import questionMarkPicture from '../../../assets/question-mark.png';
import physicalAssessmentImagesMale from '../../../config/physicalAssessmentImagesMale';
import physicalAssessmentImagesFemale from '../../../config/physicalAssessmentImagesFemale';
import SectionTitle from '../../common/SectionTitle.vue';
import CaseSpineAssessment from '../CaseSpineAssessment/CaseSpineAssessment';

const imagesByAvatar = {
  CUSTOM: physicalAssessmentImagesMale,
  AMY: physicalAssessmentImagesFemale,
  BEN: physicalAssessmentImagesMale,
  HAWTHORN: physicalAssessmentImagesMale,
  JOSHUA: physicalAssessmentImagesMale,
  PATRICIA: physicalAssessmentImagesFemale,
  SHERYL: physicalAssessmentImagesFemale,
  MILITARY_AMY: physicalAssessmentImagesFemale,
  MILITARY_BEN: physicalAssessmentImagesMale,
  MILITARY_HAWTHORN: physicalAssessmentImagesMale,
  MILITARY_JOSHUA: physicalAssessmentImagesMale,
  MILITARY_PATRICIA: physicalAssessmentImagesFemale,
  MILITARY_SHERYL: physicalAssessmentImagesFemale,
  KIM: physicalAssessmentImagesFemale,
  LIAM: physicalAssessmentImagesMale,
  MILES: physicalAssessmentImagesMale,
};

const moulageAvatars = {
  CUSTOM: 'BEN',
  AMY: 'PATRICIA',
  BEN: 'BEN',
  HAWTHORN: 'BEN',
  JOSHUA: 'BEN',
  PATRICIA: 'PATRICIA',
  SHERYL: 'PATRICIA',
  MILITARY_AMY: 'PATRICIA',
  MILITARY_BEN: 'BEN',
  MILITARY_HAWTHORN: 'BEN',
  MILITARY_JOSHUA: 'BEN',
  MILITARY_PATRICIA: 'PATRICIA',
  MILITARY_SHERYL: 'PATRICIA',
  KIM: 'PATRICIA',
  LIAM: 'BEN',
  MILES: 'BEN',
};

const moulageFileNameByLocation = {
  ABDOMEN: 'Abdomen',
  BACK_LOWER: 'Lower_Back',
  BACK_UPPER: 'Upper_Back',
  BUTTOCKS: 'Buttocks',
  CHEST: 'Chest',
  FACE: 'Face',
  NOSE: 'Nose',
  EARS: 'Ears',
  MOUTH: 'Mouth',
  GENITALIA: 'Genitalia',
  HEAD: 'Head',
  LEFT_ARM_LOWER: 'Lower_Arm_Left',
  LEFT_ARM_UPPER: 'Upper_Arm_Left',
  LEFT_FOOT: 'Foot_L',
  LEFT_HAND: 'Hand_L',
  LEFT_LEG_LOWER: 'Lower_Leg_Left',
  LEFT_LEG_UPPER: 'Upper_Leg_Left',
  NECK: 'Neck',
  RIGHT_ARM_LOWER: 'Lower_Arm_Right',
  RIGHT_ARM_UPPER: 'Upper_Arm_Right',
  RIGHT_FOOT: 'Foot_R',
  RIGHT_HAND: 'Hand_R',
  RIGHT_LEG_LOWER: 'Lower_Leg_Right',
  RIGHT_LEG_UPPER: 'Upper_Leg_Right',
  EYES: 'Eyes',
};

const structuralLinkage = {
  CHEST: [ 'ABDOMEN', 'BACK_UPPER', 'BACK_LOWER', 'GENITALIA', 'BUTTOCKS' ],
  RIGHT_ARM_UPPER: [ 'RIGHT_HAND', 'RIGHT_ARM_LOWER' ],
  RIGHT_ARM_LOWER: [ 'RIGHT_HAND', 'RIGHT_ARM_UPPER' ],
  RIGHT_HAND: [ 'RIGHT_ARM_LOWER', 'RIGHT_ARM_UPPER' ],
  LEFT_ARM_UPPER: [ 'LEFT_HAND', 'LEFT_ARM_LOWER' ],
  LEFT_ARM_LOWER: [ 'LEFT_HAND', 'LEFT_ARM_UPPER' ],
  LEFT_HAND: [ 'LEFT_ARM_LOWER', 'LEFT_ARM_UPPER' ],
  ABDOMEN: [ 'CHEST', 'BACK_UPPER', 'BACK_LOWER', 'GENITALIA', 'BUTTOCKS' ],
  BACK_UPPER: [ 'CHEST', 'ABDOMEN', 'BACK_LOWER', 'GENITALIA', 'BUTTOCKS' ],
  BACK_LOWER: [ 'CHEST', 'ABDOMEN', 'BACK_UPPER', 'GENITALIA', 'BUTTOCKS' ],
  BUTTOCKS: [ 'CHEST', 'ABDOMEN', 'BACK_UPPER', 'BACK_LOWER', 'GENITALIA' ],
  GENITALIA: [ 'CHEST', 'ABDOMEN', 'BACK_UPPER', 'BACK_LOWER', 'BUTTOCKS' ],
  RIGHT_LEG_UPPER: [ 'RIGHT_FOOT', 'RIGHT_LEG_LOWER' ],
  RIGHT_LEG_LOWER: [ 'RIGHT_FOOT', 'RIGHT_LEG_UPPER' ],
  RIGHT_FOOT: [ 'RIGHT_LEG_LOWER', 'RIGHT_LEG_UPPER' ],
  LEFT_LEG_UPPER: [ 'LEFT_FOOT', 'LEFT_LEG_LOWER' ],
  LEFT_LEG_LOWER: [ 'LEFT_FOOT', 'LEFT_LEG_UPPER' ],
  LEFT_FOOT: [ 'LEFT_LEG_LOWER', 'LEFT_LEG_UPPER' ],
  HEAD: ['FACE', 'NECK', 'NOSE', 'EARS', 'EYES', 'MOUTH'],
  FACE: ['HEAD', 'NECK', 'NOSE', 'EARS', 'EYES', 'MOUTH'],
  NECK: ['FACE', 'HEAD', 'NOSE', 'EARS', 'EYES', 'MOUTH'],
  NOSE: ['FACE', 'NECK', 'HEAD', 'EARS', 'EYES', 'MOUTH'],
  EARS: ['FACE', 'NECK', 'NOSE', 'HEAD', 'EYES', 'MOUTH'],
  EYES: ['FACE', 'NECK', 'NOSE', 'EARS', 'HEAD', 'MOUTH'],
  MOUTH: ['FACE', 'NECK', 'NOSE', 'EARS', 'EYES', 'HEAD'],
};

const locationOpposites = {
  CHEST: 'BACK_UPPER',
  ABDOMEN: 'BACK_LOWER',
  GENITALIA: 'BUTTOCKS'
};

export default {
  name: 'case-physical-assessments',
  components: {
    SectionTitle,
    CaseSpineAssessment,
  },
  props: {
    physicalAssessmentLocations: {
      type: Object,
      default: () => ({}),
    },
    caseConf: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    availableMoulages: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      state: {
        physicalAssessments: {},
        selectedMoulages: {},
        selectedStructuralMoulages: {},
        currentMoulageCategory: null,
        hoverLocationKey: null,
        currentLocationKey: null,
        isDrawerOpen: false,
        expandedMoulage: null,
        expandedLocation: null,
        expandPreviewOpen: false,
        override: {
          modalOpen: false,
          candidate: null,
          location: null,
        },
        assessment: '',
        spinalAssessments: [],
        bodyType: null,
      },
      imagesByAvatar,
      moulageAvatars,
      moulageFileNameByLocation,
      structuralLinkage,
    };
  },
  computed: {
    moulagesByCategory() {
      if (!this.state.currentLocationKey)
        return [];
      return groupBy(
        this.availableMoulages.filter(m => this.isSelectable(m, this.state.currentLocationKey)),
        'category'
      );
    },
    moulageCategories() {
      return sortBy(keys(this.moulagesByCategory), m => m);
    },
    moulagesByCategoryAndLocation() {
      if (!this.state.currentMoulageCategory || !this.state.currentLocationKey)
        return [];

      return this.moulagesByCategory[this.state.currentMoulageCategory] || [];
    },
    moulagesByCategoryAndLocationAndVariation() {
      return groupBy(this.moulagesByCategoryAndLocation, 'variation')
    },
    moulageVariations() {
      return keys(this.moulagesByCategoryAndLocationAndVariation);
    },
    moulagesById() {
      return keyBy(this.availableMoulages, 'id');
    },
    physicalAssessmentImages() {
      return imagesByAvatar[get(this.caseConf,'_avatar.key','HAWTHORN')];
    },
    language() {
      return get(this.caseConf, 'language') || 'en';
    },
    moulageAvatar() {
      return this.moulageAvatars[get(this.caseConf, '_avatar.key', 'HAWTHORN')];
    },
    moulageFileName() {
      return this.moulageFileNameByLocation[this.state.currentLocationKey];
    },
    currentLocationPhysicalRepercussion() {
      return this.getPhysicalRepercussion(this.state.currentLocationKey);
    },
    productType() {
      return this.user['https://vrpatients.com/productType'];
    },
    combinedSelectedMoulages() {
      return merge({}, this.state.selectedMoulages, this.state.selectedStructuralMoulages);
    }
  },
  watch: {
    'caseConf._avatar.id'() {
      // Watching id because avatarOptions isn't being reactive
      this.state.bodyType = this.caseConf._avatar.avatarOptions.bodyType || 'ADULT';
    }
  },
  created() {
    this.state.physicalAssessments = cloneDeep(this.caseConf.physicalAssessments) || {};
    this.state.selectedMoulages = cloneDeep(this.caseConf.moulages) || {};
    this.state.selectedStructuralMoulages = cloneDeep(this.caseConf.structuralMoulages) || {};
    this.state.spinalAssessments = cloneDeep(this.caseConf.spinalAssessments) || [];
    this.state.bodyType = this.caseConf._avatar.avatarOptions.bodyType || 'ADULT';
    this.emitUpdate = debounce(this.emitUpdate, 500);
  },
  methods: {
    isSelectable(moulage, location) {
      // Check if available
      if (moulage.unavailableRegions &&
          moulage.unavailableRegions.includes(this.state.currentLocationKey)) {
        return false;
      }
      if (this.hasPhysicalRepercussion(location)) {
        return false;
      }
      // Check if structural slot already used
      if (this.isStructuralMoulage(moulage, location)) {
        const otherLocations = this.structuralLinkage[location] || [];
        for (let i = 0; i < otherLocations.length; i++) {
          const otherMoulageId = this.state.selectedStructuralMoulages[otherLocations[i]];
          const otherMoulage = this.availableMoulages.find(m => m.id === otherMoulageId);
          const otherLocation = otherLocations[i];
          if (otherMoulage && this.isStructuralMoulage(otherMoulage, otherLocation)) {
            return false;
          }
        }
      }
      return true;
    },
    hasPhysicalAssessment(locationKey) {
       return !!this.state.physicalAssessments[locationKey] ||
                !!this.state.selectedMoulages[locationKey] ||
                !!this.state.selectedStructuralMoulages[locationKey]
    },
    hasMoulage(locationKey) {
      return !!this.state.selectedMoulages[locationKey] ||
                !!this.state.selectedStructuralMoulages[locationKey];
    },
    hasPhysicalRepercussion(locationKey) {
      return !!this.getPhysicalRepercussion(locationKey);
    },
    updateAssessment(value) {
      const key = this.state.currentLocationKey;
      this.state.physicalAssessments[key] = value || null;
      this.emitUpdate();
    },
    emitUpdate() {
      this.$emit('update', {
        physicalAssessments: this.state.physicalAssessments,
        moulages: this.state.selectedMoulages,
        structuralMoulages: this.state.selectedStructuralMoulages,
        spinalAssessments: this.state.spinalAssessments,
      });
    },
    mouseOverLocation(key) {
      this.state.hoverLocationKey = key;
    },
    mouseLeaveLocation(key) {
      if (this.state.hoverLocationKey === key) {
        this.state.hoverLocationKey = null;
      }
    },
    openLocation(locationKey) {
      this.state.currentLocationKey = locationKey;
      this.state.isDrawerOpen = true;

      if (this.currentLocationPhysicalRepercussion && this.currentLocationPhysicalRepercussion.type === 'FORCED_MOULAGE') {
        this.state.currentMoulageCategory = this.currentLocationPhysicalRepercussion.metadata.moulage.category;
      } else {
        const moulageId = this.state.selectedMoulages[locationKey] ||
                          this.state.selectedStructuralMoulages[locationKey];
        const moulage = moulageId && this.availableMoulages.find(m => m.id === moulageId);
        if (moulage) {
          this.state.currentMoulageCategory = moulage.category;
        }

        if (!this.moulageCategories.includes(this.state.currentMoulageCategory)) {
          this.state.currentMoulageCategory = undefined;
        }
        if (!this.state.currentMoulageCategory) {
          this.state.currentMoulageCategory = this.moulageCategories[0];
        }
        // Set value used by the md-textarea's vmodel property (its value property
        // doesn't reliably update the DOM)
        this.state.assessment = this.getAssessment();
      }
    },
    selectCategory(category) {
      this.state.currentMoulageCategory = category;
    },
    selectMoulage(moulage) {
      if (this.readonly) {
        return;
      }
      const location = this.state.currentLocationKey;
      const deselecting =
        this.state.selectedMoulages[location] === moulage.id ||
        this.state.selectedStructuralMoulages[location] === moulage.id;
      if (!deselecting && this.moulageDisabled(moulage).isDisabled) {
        return;
      }
      if (this.state.selectedMoulages[location] === moulage.id){
        // unset sticker moulage
        this.$set(this.state.selectedMoulages, location, null);
        this.emitUpdate();
      }
      else if (this.state.selectedStructuralMoulages[location] === moulage.id) {
        // unset structural moulage
        this.$set(this.state.selectedStructuralMoulages, location, null);
        this.emitUpdate();
      }
      else {
        if (this.confirmPhysicalRepercussion(moulage, location)) {
          this.changeMoulage(moulage);
        }
      }
    },
    changeMoulage(moulage) {
      const location = this.state.currentLocationKey;
      const { physicalRepercussions } = moulage;
      if (physicalRepercussions) {
        const forcedMoulageRepercussions = physicalRepercussions.filter(repercussion => repercussion.type === 'FORCED_MOULAGE');
        forcedMoulageRepercussions.forEach(repercussion => {
          const repercussionLocation = repercussion.location === '_OPPOSITE_' ? this.getOppositeLocation(location) : repercussion.location;
          if (repercussionLocation) {
            if (this.state.selectedMoulages[repercussionLocation]) {
              this.$set(this.state.selectedMoulages, repercussionLocation, null);
            }
            if (this.state.selectedStructuralMoulages[repercussionLocation]) {
              this.$set(this.state.selectedStructuralMoulages, repercussionLocation, null);
            }
          }
        })
      }
      if (this.isStructuralMoulage(moulage, location)) {
        this.$set(this.state.selectedStructuralMoulages, location, moulage.id);
        if (this.state.selectedMoulages[location]) {
          this.$set(this.state.selectedMoulages, location, null);
        }
      }
      else {
        this.$set(this.state.selectedMoulages, location, moulage.id);
        if (this.state.selectedStructuralMoulages[location]) {
          this.$set(this.state.selectedStructuralMoulages, location, null);
        }
      }
      this.emitUpdate();
    },
    closeModal() {
      this.state.currentLocationKey = null;
      this.state.isDrawerOpen = false;
    },
    getAssessment() {
      if (!this.state.currentLocationKey) {
        return '';
      }
      return this.state.physicalAssessments[this.state.currentLocationKey] || '';
    },
    getPhysicalRepercussion(locationKey) {
      if (!locationKey) {
        return null;
      }
      const appliedMoulages = {
        ...omitBy(this.state.selectedMoulages, v => !v),
        ...omitBy(this.state.selectedStructuralMoulages, v => !v)
      };
      return map(appliedMoulages, (moulageId, moulageLocationKey) => {
        const moulage = this.moulagesById[moulageId];
        if (!moulage) {
          return;
        }
        return find(
          moulage.physicalRepercussions,
          (repercussion) =>
            repercussion.location === locationKey ||
            (repercussion.location === '_OPPOSITE_' &&
              this.getOppositeLocation(moulageLocationKey) === locationKey)
        );
      }).find(v => !!v);
    },
    pictureFor(item, location) {
      const urlTemplate = get(item, 'picture.url', questionMarkPicture);
      const url = urlTemplate
        .replace('${avatar}', this.moulageAvatar)
        .replace(
          '${location}',
          location
            ? moulageFileNameByLocation[
                location === '_OPPOSITE_'
                  ? this.getOppositeLocation(this.state.currentLocationKey)
                  : location
              ]
            : this.moulageFileName
        );
      return url;
    },
    thumbnailFor(item, location) {
      const urlTemplate = get(item, 'thumbnail.url', questionMarkPicture);
      const url = urlTemplate
        .replace('${avatar}', this.moulageAvatar)
        .replace(
          '${location}',
          location
            ? moulageFileNameByLocation[
                location === '_OPPOSITE_'
                  ? this.getOppositeLocation(this.state.currentLocationKey)
                  : location
              ]
            : this.moulageFileName
        );
      return url;
    },
    labelFor(item) {
      return get(item, 'label.en', '');
    },
    humanize(enumString) {
      return capitalize(enumString.replace(/_/g, ' '));
    },
    getOppositeLocation(locationKey) {
      const locationInvertOpposites = invert(locationOpposites);
      return locationOpposites[locationKey] || locationInvertOpposites[locationKey] || null;
    },
    moulageSelected(moulage) {
      const locationKey = this.state.currentLocationKey;
      return (moulage.id === this.state.selectedMoulages[locationKey]) ||
             (moulage.id === this.state.selectedStructuralMoulages[locationKey]);
    },
    locationImageClasses(locationKey) {
      return {
        active: this.hasPhysicalAssessment(locationKey),
        disabled: this.hasPhysicalRepercussion(locationKey),
        hover: this.state.hoverLocationKey === locationKey,
        selected: this.state.currentLocationKey === locationKey
      }
    },
    expandPreview(moulage, location) {
      this.state.expandedMoulage = moulage;
      this.state.expandedLocation = location;
      this.state.expandPreviewOpen = true;
    },
    confirmPhysicalRepercussion(moulage, location) {
      const { physicalRepercussions } = moulage;
      if (!physicalRepercussions) {
        return true;
      }
      const forcedMoulageRepercussion = physicalRepercussions.find(repercussion => repercussion.type === 'FORCED_MOULAGE');
      if (!forcedMoulageRepercussion) {
        return true;
      }
      const physicalRepercussionLocation = forcedMoulageRepercussion.location === '_OPPOSITE_'
        ? this.getOppositeLocation(location)
        : forcedMoulageRepercussion.location;
      if (!this.hasMoulage(physicalRepercussionLocation)) {
        return true;
      }
      this.showOverrideModal(
        moulage,
        this.physicalAssessmentLocations[physicalRepercussionLocation].label.en
      );
      return false;
    },
    showOverrideModal(moulage, location) {
      this.state.override.location = location;
      this.state.override.candidate = moulage;
      this.state.override.modalOpen = true;
    },
    closeOverrideModal(doOverride) {
      if (doOverride) {
        this.changeMoulage(this.state.override.candidate);
      }
      this.state.override.location = null;
      this.state.override.candidate = null;
      this.state.override.modalOpen = false;
    },
    updateSpineAssessment(spinalAssessments) {
      this.state.spinalAssessments = spinalAssessments;
      this.emitUpdate();
    },
    moulageDisabled(moulage) {
      let result = { isDisabled: false, message: '' };
      const bodyRestrictions = moulage.bodyTypeRestrictions;
      if (bodyRestrictions && bodyRestrictions[this.state.currentLocationKey] === this.state.bodyType) {
        result.isDisabled = true;
        result.message = `This moulage is not available for the following body types:
          ${bodyTypeLabel(bodyRestrictions[this.state.currentLocationKey])}`;
      }
      if (moulage.stanceRestrictions) {
        const stanceRestrictions = moulage.stanceRestrictions['ANY'] ||
          moulage.stanceRestrictions[this.state.currentLocationKey];
        if (stanceRestrictions) {
          const positionState = this.caseConf._initialStates.find(state => state.id === 'position');
          const position = get(positionState, 'options.value');
          if (stanceRestrictions.includes(position)) {
            result.isDisabled = true;
            const stances = stanceRestrictions.map(s => positionLabel(s)).join(', ');
            result.message = `This moulage is not available in the following positions: ${stances}`;
          }
        }
      }
      const message = isInvalidMoulage(this.state.currentLocationKey, moulage.id, this.combinedSelectedMoulages);
      if (message) {
        result.isDisabled = true;
        result.message = message;
      }

      return result;
    },
    isStructuralMoulage(moulage, location) {
      if (moulage.structuralOverride && moulage.structuralOverride[location]) {
        return moulage.structuralOverride[location];
      }
      return moulage.isStructural;
    }
  },
};
</script>

<style scoped>
.physical-assessment-container {
  width: 615px;
}
.physical-assessment-locations {
  position: relative;
  height: 578px;
  width: 307px;
}
.assessment-regions {
  display: flex;
  justify-content: space-around;
}
.assessment-location {
  position: absolute;
}
.assessment-location img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
}

.assessment-location-image.selected {
  /* dark blue #618cb8 */
  -webkit-filter: brightness(0) saturate(100%) invert(54%) sepia(9%) saturate(1839%) hue-rotate(169deg) brightness(96%) contrast(92%)
                  drop-shadow(2px 2px #0F5398)
                  drop-shadow(-2px -2px #0F5398) !important;
  filter: brightness(0) saturate(100%) invert(54%) sepia(9%) saturate(1839%) hue-rotate(169deg) brightness(96%) contrast(92%)
          drop-shadow(1px 1px #0F5398)
          drop-shadow(-1px 1px #0F5398)
          drop-shadow(1px -1px #0F5398)
          drop-shadow(-1px -1px #0F5398) !important;
}
.assessment-location-image.active {
  /* green #84d586 */
  -webkit-filter: brightness(0) saturate(100%) invert(77%) sepia(11%) saturate(1383%) hue-rotate(72deg) brightness(99%) contrast(88%)
                  drop-shadow(2px 2px 0 #40883F)
                  drop-shadow(-2px -2px 0 #40883F);

  filter: brightness(0) saturate(100%) invert(77%) sepia(11%) saturate(1383%) hue-rotate(72deg) brightness(99%) contrast(88%)
          drop-shadow(1px 1px 0 #40883F)
          drop-shadow(1px -1px 0 #40883F)
          drop-shadow(-1px 1px 0 #40883F)
          drop-shadow(-1px -1px 0 #40883F);
}
.assessment-location-image.hover {
  /* light blue #cbe0ff */
  -webkit-filter: invert(25%) sepia(31%) saturate(217%) hue-rotate(180deg) brightness(107%) contrast(105%);
  filter: invert(25%) sepia(31%) saturate(217%) hue-rotate(180deg) brightness(107%) contrast(105%);
}
.assessment-location-image.disabled {
  /* green #84d586 */
  -webkit-filter:  brightness(0) saturate(100%) invert(77%) sepia(11%) saturate(1383%) hue-rotate(72deg) brightness(99%) contrast(88%)
                  drop-shadow(2px 2px 0 #40883F)
                  drop-shadow(-2px -2px 0 #40883F);

  filter:  brightness(0) saturate(100%) invert(77%) sepia(11%) saturate(1383%) hue-rotate(72deg) brightness(99%) contrast(88%)
          drop-shadow(1px 1px 0 #40883F)
          drop-shadow(1px -1px 0 #40883F)
          drop-shadow(-1px 1px 0 #40883F)
          drop-shadow(-1px -1px 0 #40883F);
}

.patient-condition-drawer {
  width: 670px;
}
.divider {
  border-top: solid 2px #2b2b2b;
}

.md-textarea {
  resize: none !important;
  height: 216px;
  width: 100%;
}
.assessment-caption {
  margin-top: 8px;
  display: block;
}

.md-dialog-actions button {
  min-width: 120px !important;
}

.md-tooltip {
  color: white;
  background-color: #3f83c5;
  font-weight: 500;
  font-size: 15px;
  line-height: 18px;
  background: #262736;
  border-radius: 5px;
  height: 27px;
  display: flex;
  align-items: center;
  text-align: center;
}
.md-tooltip[x-placement=right]::after {
  content: " ";
  position: absolute;
  top: 50%;
  right: 100%; /* To the left of the tooltip */
  margin-top: -1px;
  border: 1px solid #000000;
  width: 35px;
}
.md-tooltip[x-placement=left]::after {
  content: " ";
  position: absolute;
  top: 50%;
  left: 100%; /* To the right of the tooltip */
  margin-top: -1px;
  border: 1px solid #000000;
  width: 35px;
}
.md-tooltip.no-arrow[x-placement=left]::after {
  border: none !important;
}
.category-select {
  width: fit-content;
}
.moulage-button {
  margin-left: 0;
  width: 64px;
  height: 84px;
  display: inline-block;
  margin-right: 10px;
  vertical-align: top;
  cursor: pointer;
}
.moulage-button >>> span {
  display: block;
}
.moulage-button >>> img {
  width: 64px;
  height: 64px;
}
.moulage-button.selected >>> img {
  outline: 3px solid #3f83c5;
}
.moulage-picture {
  width: 512px;
}
.expand-preview-button {
  position: absolute;
  top: 0px;
  right: -6px;
  width: 28px;
  height: 28px;
  min-width: 28px;
}
.moulage-button {
  position: relative;
  width: 64px;
  height: 84px;
  text-align: center;
}
.lower-drawer-content {
  overflow: visible;
}
.assessment-columns {
  display: flex;
}
.assessment-column {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.assessment-column:last-child {
  padding-left: 84px;
  padding-right: 16px;
}
.thumbnail-disabled {
  opacity: .4;
  cursor: default;
}
</style>

<style>
.md-overlay.md-dialog-overlay {
  z-index: 9; /* fix for modal overlay to take precedence over flyout overlay */
}
</style>
